module Sm.Service.App

open System
open System.Runtime.CompilerServices

open Browser
open BrowserNative
open Fable.Core
open Fable.Core.JsInterop
open MobF

open Sm.Core.Interop
open Sm.Core.Interop.Operators

type ReloadSwitch =
    | Off
    | On of string

let reloadSwitch =
    Model.useInit (fun () -> Off)
    |> Model.andUpdate (fun _ x -> x)
    |> Model.create

module CriticalError =
    type NamedError = string * exn

    type State = {
        Error: NamedError option
    }

    type Msg =
        | Set of NamedError

    let create () =
        let init () =
            { Error = None }

        let update state = function
            | Set error -> { state with Error = Some error }

        Model.useInit init
        |> Model.andUpdate update
        |> Model.create

    let instance = create ()

type CriticalPromiseBuilder(name) =
    inherit Promise.PromiseBuilder()

    member _.Run(p: JS.Promise<'T>): JS.Promise<'T> =
        base.Run(p)
        |> Promise.catchBind (fun e ->
            CriticalError.instance.Post(CriticalError.Set (name, e))
            Promise.reject e
        )

type CriticalScope(scope) =
    let formatName = function
        | None -> scope
        | Some m -> $"{scope}.%s{m}"

    member _.Promise([<CallerMemberName>] ?memberName) =
        CriticalPromiseBuilder(formatName memberName)

type User = {
    ValidAt: DateTime
    Id: string
    Name: string
}

type Registration = {
    Name: string
    Email: string
    Password: string
    PasswordConfirm: string
}

module User =
    let inline private unknownError action =
        Error [sprintf "Unable to %s at this time, if the problem persists, please contact support" action]

    let private parseUser (record: Pocketbase.AuthRecord) =
        {
            ValidAt = DateTime.Now
            Id = record.id
            Name = record?name
        }

    type Request<'T> = {
        Action: string
        Handler: Pocketbase.RecordService<obj> -> JS.Promise<Result<'T, string list>>
        MapStatusMessage: int -> string option
        MapValidationMessage: (string * string -> string option)
    }

    let private execute request (client: Pocketbase.Client) =
        let unknownError = unknownError request.Action

        promise {
            try
                let users = client.collection("users")
                match! request.Handler users with
                | Ok result -> return Ok result
                | Error messages -> return Error messages
            with e ->
                let status: float = e?status
                let status = int status

                match request.MapStatusMessage status with
                | Some message -> return Error [message]
                | None ->
                    match status with
                    | 400 ->
                        return
                            (e?response) @ "data"
                            |> Option.map objectEntries
                            |> Option.defaultValue Seq.empty
                            |> Seq.choose (fun (key, value) ->
                                match value @ "message" with
                                | Some (JsString m) -> Some (key, m)
                                | _ -> None
                            )
                            |> Seq.map (fun (k, v) ->
                                match request.MapValidationMessage (k, v) with
                                | Some m -> m
                                | None -> v
                            )
                            |> List.ofSeq
                            |> function
                                | [] -> unknownError
                                | messages -> Error messages
                    | 0 ->
                        return Error [sprintf "Unable to %s at this time, the server could not be reached" request.Action]
                    | _ ->
                        return unknownError
        }

    let current (client: Pocketbase.Client) =
        let auth = client.authStore
        if auth.isValid then
            Some (parseUser auth.record)
        else
            None

    let authenticate email password client =
        let action = "authenticate"
        client |> execute {
            Action = action
            Handler = (fun users ->
                promise {
                    let! _ = users.authWithPassword(email, password)

                    match client |> current with
                    | Some user -> return Ok user
                    | None -> return unknownError action
                }
            )
            MapStatusMessage = function
                | 400 -> Some "No account matches the given email and password"
                | _ -> None
            MapValidationMessage = fun _ -> None
        }

    let register registration client =
        client |> execute {
            Action = "register"
            Handler = (fun users ->
                promise {
                    let options = createEmpty<Pocketbase.RecordOptions>

                    let body =
                        createObj [
                            "name" ==> registration.Name
                            "email" ==> registration.Email
                            "password" ==> registration.Password
                            "passwordConfirm" ==> registration.PasswordConfirm
                        ]
                        |> Some
                        |> U2<obj option, Types.FormData>.Case1

                    let! _ = users.create<obj>(body, options)
                    return! client |> authenticate registration.Email registration.Password
                }
            )
            MapStatusMessage = fun _ -> None
            MapValidationMessage = function
                | "name", msg -> Some ("Name: " + msg)
                | "email", msg -> Some ("Email: " + msg)
                | "password", msg -> Some ("Password: " + msg)
                | "passwordConfirm", msg -> Some ("Confirm Password: " + msg)
                | _ -> None
        }

    let requestPasswordReset email client =
        let action = "request password reset"
        client |> execute {
            Action = action
            Handler = (fun users ->
                promise {
                    match! users.requestPasswordReset(email) with
                    | true -> return Ok ()
                    | false -> return unknownError action
                }
            )
            MapStatusMessage = fun _ -> None
            MapValidationMessage = function
                | "email", msg -> Some ("Email: " + msg)
                | _ -> None
        }

    let confirmPasswordReset token password passwordConfirm client =
        let action = "reset password"
        client |> execute {
            Action = action
            Handler = (fun users ->
                promise {
                    match! users.confirmPasswordReset(token, password, passwordConfirm) with
                    | true -> return Ok ()
                    | false -> return unknownError action
                }
            )
            MapStatusMessage = fun _ -> None
            MapValidationMessage = function
                | "password", msg -> Some ("New Password: " + msg)
                | "passwordConfirm", msg -> Some ("Confirm New Password: " + msg)
                | _ -> None
        }

    let signout (client: Pocketbase.Client) =
        client.authStore.clear()

    let refresh (client: Pocketbase.Client) =
        client.collection("users").authRefresh()
        |> Promise.map (fun x -> parseUser x.record)

module Settings =
    let private autoFocusKey = "auto-focus"

    type State = {
        Version: int
    }

    type Msg =
        | SetAutoFocus of bool option

    type Computed =
        abstract AutoFocus: bool

    let create () =
        let init () =
            {
                Version = 0
            }

        let update state = function
            | SetAutoFocus value ->
                match value with
                | None ->  localStorage.removeItem(autoFocusKey)
                | Some true -> localStorage.setItem(autoFocusKey, "true")
                | Some false -> localStorage.setItem(autoFocusKey, "false")
                { state with Version = state.Version + 1 }

        let compute state =
            { new Computed with
                member _.AutoFocus =
                    ignore state.Version

                    match localStorage.getItem(autoFocusKey) with
                    | "true" -> true
                    | "false" -> false
                    | _ ->
                        if Capabilities.hasTouchscreen then false
                        else true
            }
            
        Model.useInit init
        |> Model.andUpdate update
        |> Model.createWithComputed compute

    let current = create ()
